import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import CustomSelect from "../../component/CustomSelect";
import DataTable from "../../component/DataTable";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import Icons from "../../myIcon/Icons";
import { StatusData, formatPermissions, formatStatus } from "./RbacOptions";

const endpoints = ApiEndpoints.rbac;

const defaultForm = {
  name: "",
  permission_ids: "",
  status: "1",
};

function RoleModal({ show, onHide, roleId, permissions, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const isEdit = Boolean(roleId);

  useEffect(() => {
    if (!show) return;
    if (!roleId) {
      setForm(defaultForm);
      return;
    }

    ApiRequest("GET", `${endpoints.roleEdit}${roleId}`)
      .then((response) => setForm({
        name: response.role?.name || "",
        permission_ids: (response.role?.permission_ids || []).join(","),
        status: String(response.role?.status ?? "1"),
      }))
      .catch(() => toast.error("Unable to fetch role."));
  }, [show, roleId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Role name is required.");
      return;
    }

    try {
      const endpoint = isEdit ? `${endpoints.roleUpdate}${roleId}` : endpoints.roleCreate;
      const response = await ApiRequest("POST", endpoint, form);
      if (response.error) {
        toast.error(response.message);
        return;
      }
      await onSaved();
      toast.success(response.message);
      onHide();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save role.");
    }
  };

  return (
    <Modal className="add-modal-area" show={show} onHide={onHide} size="md" centered>
      <Modal.Body>
        <button className="modal-close-button" onClick={onHide}><Icons name="cross" /></button>
        <form onSubmit={handleSubmit}>
          <div className="add-page-sections">{isEdit ? "Edit Role" : "Create Role"}</div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Name<small>*</small></label></div>
            <input name="name" value={form.name} className="form-control" placeholder="Enter role name" onChange={handleChange} required />
          </div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Permissions</label></div>
            <CustomSelect name="permission_ids" options={permissions} defaultValue={form.permission_ids ? form.permission_ids.split(",") : []} multiple placeholder="Select Permissions" onChange={handleChange} />
          </div>
          <div className="add-page-inputs">
            <div className="label-wrap-add"><label>Status<small>*</small></label></div>
            <CustomSelect name="status" options={StatusData} defaultValue={form.status} onChange={handleChange} />
          </div>
          <button type="submit" className="default-btn">save<span></span></button>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default function Roles() {
  const [data, setData] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [modalShow, setModalShow] = useState(false);

  const dataFetch = async () => {
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        ApiRequest("GET", endpoints.roleList),
        ApiRequest("GET", endpoints.permissionList),
      ]);
      setPermissions(permissionsResponse.permissions || []);
      setData((rolesResponse.roles || []).map((item) => ({
        ...item,
        permissionsText: formatPermissions(item.permissions),
        statusText: formatStatus(item.status),
      })));
    } catch (error) {
      toast.error("Unable to fetch roles.");
    }
  };

  useEffect(() => {
    dataFetch();
  }, []);

  const handleDelete = async (role_id) => {
    if (!window.confirm("Delete this role? Users assigned to it will lose this role.")) return;
    const response = await ApiRequest("POST", endpoints.roleDelete, { role_id });
    if (response.error) {
      toast.error(response.message);
      return;
    }
    await dataFetch();
    toast.success(response.message);
  };

  const openCreate = () => {
    setRoleId("");
    setModalShow(true);
  };

  return (
    <>
      <AdminHeader pagename={{ name: "User Management", user: "Admin", innerpage: "User Management / Roles" }} />
      <div className="add-btn-section">
        <Link className="admin-add-btn" onClick={openCreate}> Add +</Link>
        <DataTable
          data={data}
          tableHeader={["Date", "Name", "Permissions", "Status"]}
          tbody={["createdAt", "name", "permissionsText", "statusText"]}
          setModalShow={setModalShow}
          setId={setRoleId}
          onDelete={handleDelete}
          tableId="rbac-roles-table"
        />
      </div>
      <RoleModal show={modalShow} roleId={roleId} permissions={permissions} onHide={() => { setModalShow(false); setRoleId(""); }} onSaved={dataFetch} />
    </>
  );
}
